import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  LEVEL_LABELS,
  LEVELS,
  RIGHTS_BASIS,
  RIGHTS_LABELS,
  UPLOAD_LIMITS,
  type EbookPageView,
  type EbookSegmentView,
  type EbookSummary,
  type PageBlock,
} from "@book2class/shared";
import { api, ApiError, formatBytes, formatDate, uploadWithProgress } from "../api";
import { Field, Spinner } from "../components/ui";
import { GenerateCard } from "./generation-pages";

// ─────────────────────────── Situação do processamento ───────────────────────────

function StatusBadge({ e }: { e: Pick<EbookSummary, "status" | "progress"> }) {
  if (e.status === "ready")
    return (
      <span className="badge ok">
        <span className="dot" /> Texto extraído
      </span>
    );
  if (e.status === "failed")
    return (
      <span className="badge error">
        <span className="dot" /> Falha
      </span>
    );
  return (
    <span className="badge warn">
      <span className="dot pulse" />{" "}
      {e.status === "uploaded" ? "Na fila" : (e.progress?.step ?? "Processando")}
    </span>
  );
}

const isActive = (e: Pick<EbookSummary, "status">) => e.status === "uploaded" || e.status === "processing";

// ─────────────────────────── Lista ───────────────────────────

export function EbookListPage() {
  const [items, setItems] = useState<EbookSummary[] | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api
      .get<EbookSummary[]>("/ebooks")
      .then(setItems)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao carregar."));
  }, []);

  useEffect(load, [load]);
  useEffect(() => {
    if (!items?.some(isActive)) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [items, load]);

  return (
    <main className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Área do professor</div>
          <h1>Seus ebooks</h1>
          <p>Cada ebook enviado dá origem a um curso em rascunho.</p>
        </div>
        <Link to="/professor/ebooks/novo" className="btn">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Enviar ebook
        </Link>
      </div>
      {error && <p className="alert error">{error}</p>}
      {!items ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="card empty">
          <h2>Nenhum ebook por aqui ainda</h2>
          <p>
            Envie um PDF de até {UPLOAD_LIMITS.maxPages} páginas para começar a montar seu primeiro curso.
          </p>
          <Link to="/professor/ebooks/novo" className="btn">
            Enviar o primeiro ebook
          </Link>
        </div>
      ) : (
        <div className="ebook-list">
          {items.map((e) => (
            <Link key={e.id} to={`/professor/ebooks/${e.id}`} className="card ebook-row">
              <span className="book-icon">PDF</span>
              <span style={{ minWidth: 0 }}>
                <span className="title">{e.courseTitle ?? e.originalName}</span>
                <span className="meta" style={{ display: "block" }}>
                  {e.originalName} · {e.pageCount ?? "?"} páginas · {formatBytes(e.sizeBytes)} ·{" "}
                  {formatDate(e.createdAt)}
                </span>
              </span>
              <span className="status">
                <StatusBadge e={e} />
              </span>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}

// ─────────────────────────── Envio ───────────────────────────

export function EbookUploadPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [drag, setDrag] = useState(false);
  const [form, setForm] = useState({
    title: "",
    audience: "",
    level: "beginner",
    tone: "",
    workloadHours: "",
    rightsBasis: "",
    rightsAccepted: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [progress, setProgress] = useState<number | null>(null);

  function pick(f: File | undefined) {
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      setErrors({ ...errors, file: "Selecione um arquivo PDF." });
      return;
    }
    if (f.size > UPLOAD_LIMITS.maxPdfBytes) {
      setErrors({
        ...errors,
        file: `O arquivo tem ${formatBytes(f.size)}; o limite é ${formatBytes(UPLOAD_LIMITS.maxPdfBytes)}.`,
      });
      return;
    }
    setErrors({ ...errors, file: "" });
    setFile(f);
    if (!form.title)
      setForm((s) => ({
        ...s,
        title: f.name
          .replace(/\.pdf$/i, "")
          .replace(/[_-]+/g, " ")
          .trim(),
      }));
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDrag(false);
    pick(e.dataTransfer.files[0]);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!file) {
      setErrors({ file: "Selecione o arquivo PDF do ebook." });
      return;
    }
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("audience", form.audience);
    fd.append("level", form.level);
    fd.append("tone", form.tone);
    if (form.workloadHours) fd.append("workloadHours", form.workloadHours);
    fd.append("rightsBasis", form.rightsBasis);
    fd.append("rightsAccepted", String(form.rightsAccepted));
    fd.append("file", file);
    setProgress(0);
    try {
      const created = await uploadWithProgress<EbookSummary>("/ebooks", fd, setProgress);
      navigate(`/professor/ebooks/${created.id}`);
    } catch (err) {
      setProgress(null);
      if (err instanceof ApiError) {
        setError(err.message);
        setErrors(err.fields);
      } else setError("Falha no envio. Tente novamente.");
    }
  }

  const sending = progress !== null;
  return (
    <main className="page" style={{ maxWidth: 760 }}>
      <div className="page-head">
        <div>
          <div className="eyebrow">
            <Link to="/professor/ebooks">Ebooks</Link> / Novo
          </div>
          <h1>Enviar ebook</h1>
          <p>
            O texto é extraído automaticamente; páginas escaneadas passam por reconhecimento óptico (OCR).
          </p>
        </div>
      </div>
      <form className="card card-pad form" onSubmit={submit} noValidate>
        {error && <p className="alert error">{error}</p>}

        <div className="field">
          <span className="label">Arquivo PDF</span>
          {file ? (
            <div className="file-pill">
              <span className="book-icon" style={{ width: 34, height: 42 }}>
                PDF
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span className="name" style={{ display: "block" }}>
                  {file.name}
                </span>
                <span className="size">{formatBytes(file.size)}</span>
              </span>
              {!sending && (
                <button type="button" className="btn ghost small" onClick={() => setFile(null)}>
                  Trocar
                </button>
              )}
            </div>
          ) : (
            <div
              className={`dropzone${drag ? " active" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDrag(true);
              }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              aria-invalid={errors.file ? true : undefined}
            >
              <svg
                width="34"
                height="34"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              >
                <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
              </svg>
              <strong>Arraste o PDF aqui ou toque para escolher</strong>
              <span style={{ fontSize: "0.85rem" }}>
                Até {formatBytes(UPLOAD_LIMITS.maxPdfBytes)} e {UPLOAD_LIMITS.maxPages} páginas
              </span>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            tabIndex={-1}
            onChange={(e) => pick(e.target.files?.[0])}
          />
          {errors.file && <span className="error">{errors.file}</span>}
        </div>

        <div className="form-section form">
          <Field
            label="Título do curso"
            value={form.title}
            error={errors.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <Field
            label="Público-alvo"
            placeholder="Ex.: microempreendedores sem formação em finanças"
            value={form.audience}
            error={errors.audience}
            onChange={(e) => setForm({ ...form, audience: e.target.value })}
          />
          <div className="grid-2">
            <div className="field">
              <label htmlFor="level">Nível</label>
              <select
                id="level"
                value={form.level}
                onChange={(e) => setForm({ ...form, level: e.target.value })}
              >
                {LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {LEVEL_LABELS[l]}
                  </option>
                ))}
              </select>
            </div>
            <Field
              label="Carga horária estimada (horas)"
              type="number"
              min={1}
              max={400}
              inputMode="numeric"
              value={form.workloadHours}
              error={errors.workloadHours}
              onChange={(e) => setForm({ ...form, workloadHours: e.target.value })}
            />
          </div>
          <Field
            label="Tom desejado"
            placeholder="Ex.: didático e próximo, com exemplos do dia a dia"
            value={form.tone}
            error={errors.tone}
            onChange={(e) => setForm({ ...form, tone: e.target.value })}
          />
        </div>

        <fieldset className="form-section form" style={{ border: 0, margin: 0, padding: "20px 0 0" }}>
          <legend className="label" style={{ padding: 0, marginBottom: 8 }}>
            Direitos sobre o ebook
          </legend>
          <div className="radio-cards">
            {RIGHTS_BASIS.map((b) => (
              <label key={b} className="radio-card">
                <input
                  type="radio"
                  name="rightsBasis"
                  value={b}
                  checked={form.rightsBasis === b}
                  onChange={() => setForm({ ...form, rightsBasis: b })}
                />
                <span>{RIGHTS_LABELS[b]}</span>
              </label>
            ))}
          </div>
          {errors.rightsBasis && <span className="error">{errors.rightsBasis}</span>}
          <label className="check">
            <input
              type="checkbox"
              checked={form.rightsAccepted}
              onChange={(e) => setForm({ ...form, rightsAccepted: e.target.checked })}
            />
            <span>
              Declaro que as informações acima são verdadeiras, que o direito de uso abrange a transformação
              em curso, inclusive a narração em áudio por voz sintética, e assumo a responsabilidade pelo uso
              do conteúdo na plataforma.
            </span>
          </label>
          {errors.rightsAccepted && <span className="error">{errors.rightsAccepted}</span>}
        </fieldset>

        {sending && (
          <div aria-live="polite">
            <div className="progress" aria-hidden>
              <span style={{ width: `${progress}%` }} />
            </div>
            <p className="segment-meta" style={{ marginTop: 6 }}>
              {progress < 100 ? `Enviando… ${progress}%` : "Validando o arquivo…"}
            </p>
          </div>
        )}
        <div className="actions">
          <Link to="/professor/ebooks" className="btn ghost">
            Cancelar
          </Link>
          <button className="btn" disabled={sending}>
            {sending ? "Enviando…" : "Enviar e extrair texto"}
          </button>
        </div>
      </form>
    </main>
  );
}

// ─────────────────────────── Detalhe ───────────────────────────

type Detail = EbookSummary & { segments: Array<Omit<EbookSegmentView, "text">> };

function Blocks({ blocks }: { blocks: PageBlock[] }) {
  const out: ReactNode[] = [];
  let list: string[] = [];
  const flush = (key: string) => {
    if (list.length)
      out.push(
        <ul key={key}>
          {list.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>,
      );
    list = [];
  };
  blocks.forEach((b, i) => {
    if (b.kind === "list_item") return void list.push(b.text);
    flush(`l${i}`);
    if (b.kind === "heading")
      out.push(
        <h4 key={i} className={`lvl-${b.level ?? 2}`}>
          {b.text}
        </h4>,
      );
    else if (b.kind === "table_row")
      out.push(
        <div key={i} className="table-row">
          {b.text}
        </div>,
      );
    else out.push(<p key={i}>{b.text}</p>);
  });
  flush("end");
  return <div className="reading">{out}</div>;
}

function SegmentText({ ebookId, position }: { ebookId: string; position: number }) {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    api
      .get<EbookSegmentView[]>(`/ebooks/${ebookId}/segments`)
      .then((all) => setText(all.find((s) => s.position === position)?.text ?? ""))
      .catch(() => setText(""));
  }, [ebookId, position]);
  if (text === null) return <Spinner />;
  const blocks: PageBlock[] = text.split(/\n\n/).map((chunk) => {
    const h = /^(#{2,4}) (.*)$/.exec(chunk);
    if (h) return { kind: "heading", text: h[2]!, level: h[1]!.length - 1 };
    if (chunk.startsWith("- ")) return { kind: "list_item", text: chunk.slice(2) };
    if (chunk.includes(" | ")) return { kind: "table_row", text: chunk };
    return { kind: "paragraph", text: chunk };
  });
  return <Blocks blocks={blocks} />;
}

const PAGE_SIZE = 5;

function PagesView({ ebookId, total }: { ebookId: string; total: number }) {
  const [from, setFrom] = useState(1);
  const [loaded, setLoaded] = useState<{ from: number; pages: EbookPageView[] } | null>(null);
  useEffect(() => {
    api
      .get<{ pages: EbookPageView[] }>(`/ebooks/${ebookId}/pages?from=${from}&to=${from + PAGE_SIZE - 1}`)
      .then((r) => setLoaded({ from, pages: r.pages }))
      .catch(() => setLoaded({ from, pages: [] }));
  }, [ebookId, from]);
  const pages = loaded?.from === from ? loaded.pages : null;
  const to = Math.min(total, from + PAGE_SIZE - 1);
  const pager = (
    <div className="pager">
      <button
        className="btn ghost small"
        disabled={from <= 1}
        onClick={() => setFrom(Math.max(1, from - PAGE_SIZE))}
      >
        ← Anteriores
      </button>
      <span>
        Páginas {from}–{to} de {total}
      </span>
      <button className="btn ghost small" disabled={to >= total} onClick={() => setFrom(from + PAGE_SIZE)}>
        Próximas →
      </button>
    </div>
  );
  return (
    <div>
      {pager}
      <div style={{ marginTop: 16 }}>
        {!pages ? (
          <Spinner />
        ) : (
          pages.map((p) => (
            <article key={p.pageNumber} className="card page-card">
              <div className="page-card-head">
                <strong style={{ color: "var(--ink)" }}>Página {p.pageNumber}</strong>
                {p.source === "ocr" ? (
                  <span className="badge brand">OCR · confiança {Math.round(p.ocrConfidence ?? 0)}%</span>
                ) : p.source === "empty" ? (
                  <span className="badge">Sem texto</span>
                ) : null}
              </div>
              {p.blocks.length ? (
                <Blocks blocks={p.blocks} />
              ) : (
                <p className="segment-meta">Página sem conteúdo textual.</p>
              )}
            </article>
          ))
        )}
      </div>
      {pages && pages.length > 0 && pager}
    </div>
  );
}

export function EbookDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<"segments" | "pages">("segments");

  const load = useCallback(() => {
    api
      .get<Detail>(`/ebooks/${id}`)
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao carregar."));
  }, [id]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!data || !isActive(data)) return;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [data, load]);

  if (error)
    return (
      <main className="page">
        <p className="alert error">{error}</p>
        <Link to="/professor/ebooks">Voltar aos ebooks</Link>
      </main>
    );
  if (!data)
    return (
      <main className="page">
        <Spinner />
      </main>
    );

  const p = data.progress;
  const pct = p?.total ? Math.round(((p.done ?? 0) / p.total) * 100) : 0;
  const tokens = data.segments.reduce((n, s) => n + s.tokenEstimate, 0);

  return (
    <main className="page">
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          <div className="eyebrow">
            <Link to="/professor/ebooks">Ebooks</Link> / Conteúdo extraído
          </div>
          <h1>{data.courseTitle ?? data.originalName}</h1>
          <p>
            {data.originalName} · enviado em {formatDate(data.createdAt)}
          </p>
        </div>
        <StatusBadge e={data} />
      </div>

      {isActive(data) && (
        <div className="card card-pad" aria-live="polite" style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: "1.1rem" }}>
            {data.status === "uploaded" ? "Aguardando processamento" : (p?.step ?? "Processando")}
          </h2>
          <div className="progress">
            <span style={{ width: `${data.status === "uploaded" ? 2 : Math.max(4, pct)}%` }} />
          </div>
          <p className="segment-meta" style={{ marginTop: 8, marginBottom: 0 }}>
            {p?.total
              ? `${p.done} de ${p.total}`
              : "Isso leva de alguns segundos a poucos minutos, conforme o tamanho do PDF."}
          </p>
        </div>
      )}

      {data.status === "failed" && (
        <div className="alert error" style={{ marginBottom: 24 }}>
          <p style={{ marginBottom: 10 }}>{data.error ?? "Falha no processamento."}</p>
          <div className="actions" style={{ justifyContent: "flex-start" }}>
            <button
              className="btn small"
              onClick={async () => {
                await api.post(`/ebooks/${id}/reprocess`).catch(() => undefined);
                load();
              }}
            >
              Tentar novamente
            </button>
            <button
              className="btn ghost small"
              onClick={async () => {
                if (!confirm("Excluir este ebook e o texto extraído?")) return;
                await api.del(`/ebooks/${id}`);
                navigate("/professor/ebooks");
              }}
            >
              Excluir
            </button>
          </div>
        </div>
      )}

      {data.status === "ready" && data.courseId && <GenerateCard courseId={data.courseId} />}

      {data.status === "ready" && (
        <>
          <div className="stats">
            <div className="card stat">
              <div className="k">Páginas</div>
              <div className="v">{data.pageCount}</div>
            </div>
            <div className="card stat">
              <div className="k">Segmentos</div>
              <div className="v">{data.segments.length}</div>
            </div>
            <div className="card stat">
              <div className="k">Páginas com OCR</div>
              <div className="v">{data.ocrPages}</div>
            </div>
            <div className="card stat">
              <div className="k">Tokens estimados</div>
              <div className="v">{tokens.toLocaleString("pt-BR")}</div>
            </div>
          </div>
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={tab === "segments"} onClick={() => setTab("segments")}>
              Segmentos
            </button>
            <button role="tab" aria-selected={tab === "pages"} onClick={() => setTab("pages")}>
              Texto por página
            </button>
          </div>
          {tab === "segments" ? (
            <div>
              {data.segments.map((s) => (
                <section key={s.position} className="card segment">
                  <div className="segment-head">
                    <h3>
                      {s.position}. {s.title}
                    </h3>
                    <span className="segment-meta">
                      {s.pageStart === s.pageEnd ? `p. ${s.pageStart}` : `p. ${s.pageStart}–${s.pageEnd}`} ·{" "}
                      {s.charCount.toLocaleString("pt-BR")} caracteres
                    </span>
                  </div>
                  <details>
                    <summary>Ver texto do segmento</summary>
                    <div style={{ marginTop: 12 }}>
                      <SegmentText ebookId={id} position={s.position} />
                    </div>
                  </details>
                </section>
              ))}
            </div>
          ) : (
            <PagesView ebookId={id} total={data.pageCount ?? 0} />
          )}
        </>
      )}
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main className="page center">
      <div className="empty">
        <h1>Página não encontrada</h1>
        <Link to="/">Voltar ao início</Link>
      </div>
    </main>
  );
}
